import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  notificationService,
  type CriticalAlertState,
  type NotificationItem,
} from '../../services/notifications';
import { formatRelativeTime } from '../../utils/dateFormatters';
import AlertOutcomeDialog from './AlertOutcomeDialog';
import SaveForLaterDialog from './SaveForLaterDialog';
import styles from './CriticalAlertBar.module.css';

/** "Saved for later until 10/02 (Emily): note" for a muted row. */
export function savedForLaterLabel(alert: NotificationItem): string {
  const who = (alert.saved_for_later_by || 'you').split('@')[0];
  let text = 'Saved for later';
  if (alert.saved_for_later_until) {
    const [y, m, d] = alert.saved_for_later_until.split('-').map(Number);
    text += ` until ${m}/${d}/${String(y).slice(2)}`;
  }
  text += ` (${who})`;
  if (alert.saved_for_later_note) text += `: ${alert.saved_for_later_note}`;
  return text;
}

const POLL_INTERVAL_MS = 30000;

/**
 * Compact, expandable pop-up bar for CRITICAL must-acknowledge alerts (2026-06-09
 * Scott+PJ meeting; condensed 2026-06-22 per feedback). Intended consumer: the
 * missed-backorder / SHORT_SHIP detector.
 *
 * Collapsed it occupies a SINGLE row showing the most-recent alert plus a bold count
 * pill for the rest, so a backlog never eats the screen; click the row to expand the
 * full (scrollable) list. Each alert KEEPS showing until a human clicks Acknowledge —
 * there is deliberately NO dismiss escape hatch (read/dismiss do not clear a critical
 * alert, only acknowledgement does; "if they don't acknowledge it, it keeps showing
 * up"). Renders nothing when there are no unacknowledged critical alerts.
 * Self-contained: polls /api/notifications/critical.
 */
export function CriticalAlertBar() {
  const { isAuthenticated } = useAuth();
  const [alerts, setAlerts] = useState<NotificationItem[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [acking, setAcking] = useState<Set<string>>(new Set());
  // Audit-loop alerts must be cleared with an outcome rather than a plain
  // acknowledge. The backend decides which ones — the rule lives in exactly
  // one place so this UI cannot drift out of sync with the server guard.
  const [outcomeRequired, setOutcomeRequired] = useState<Set<string>>(new Set());
  const [resolving, setResolving] = useState<NotificationItem | null>(null);
  // Save-for-later (owner ruling, review fmbdf6): state per alert comes from
  // the backend so the come-back-date clock lives in one place.
  const [states, setStates] = useState<Record<string, CriticalAlertState>>({});
  const [returnedNotes, setReturnedNotes] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<NotificationItem | null>(null);
  // Messages are kept to the 220-character contract at the boundary; the
  // full write-up (extra_data.full_message) opens on demand.
  const [expandedMsg, setExpandedMsg] = useState<Set<string>>(new Set());
  const [saveSubmitting, setSaveSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await notificationService.getCriticalAlerts();
      // Guard the shape: this bar lives in the portal shell, so a poll that
      // answers without `alerts` must degrade to "no alerts", never throw
      // `alerts.length` inside the render (kanban 90f3b9d8).
      setAlerts(Array.isArray(res?.alerts) ? res.alerts : []);
      setOutcomeRequired(new Set(res.outcome_required_ids ?? []));
      setStates(res.alert_states ?? {});
      setReturnedNotes(res.returned_notes ?? {});
    } catch (err) {
      // Silent — a failed poll must never break the portal shell.
      console.error('Failed to fetch critical alerts:', err);
    }
  }, []);

  const handleResolved = useCallback((id: string) => {
    setResolving(null);
    // Optimistically remove; the next poll reconciles with the server.
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchAlerts();
    const interval = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchAlerts]);

  const handleAcknowledge = useCallback(async (id: string) => {
    setAcking((prev) => new Set(prev).add(id));
    try {
      await notificationService.acknowledge(id);
      // Optimistically remove; the next poll reconciles with the server.
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Failed to acknowledge critical alert:', err);
    } finally {
      setAcking((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  const handleSaveForLater = useCallback(async (until: string | null, note: string) => {
    if (!saving) return;
    setSaveSubmitting(true);
    setSaveError(null);
    try {
      const updated = await notificationService.saveForLater(saving.id, until, note || null);
      setAlerts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setStates((prev) => ({ ...prev, [updated.id]: 'saved_for_later' }));
      setReturnedNotes((prev) => {
        const next = { ...prev };
        delete next[updated.id];
        return next;
      });
      setSaving(null);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setSaveError(typeof detail === 'string' ? detail : 'Could not save this alert for later.');
    } finally {
      setSaveSubmitting(false);
    }
  }, [saving]);

  const handleBringBack = useCallback(async (id: string) => {
    try {
      const updated = await notificationService.bringBack(id);
      setAlerts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setStates((prev) => ({ ...prev, [id]: 'active' }));
    } catch (err) {
      console.error('Failed to bring the alert back:', err);
    }
  }, []);

  if (!isAuthenticated || alerts.length === 0) return null;

  const stateOf = (a: NotificationItem): CriticalAlertState =>
    states[a.id] ?? (a.saved_for_later_at && !a.saved_for_later_returned_at ? 'saved_for_later' : 'active');
  const active = alerts.filter((a) => stateOf(a) !== 'saved_for_later');
  const allSaved = active.length === 0;
  const top = active[0] ?? alerts[0];
  const more = alerts.length - 1;

  return (
    <div
      className={`${styles.bar} ${allSaved ? styles.barSaved : ''}`}
      role="alert"
      aria-live={allSaved ? 'polite' : 'assertive'}
      data-testid="critical-alert-bar"
      data-all-saved={String(allSaved)}
    >
      {/* Collapsed summary — one row tall; click to expand the full list. */}
      <button
        type="button"
        className={styles.summary}
        data-action="critical_alert_bar_toggle"
        data-target-notification-id={top.id}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="critical-alert-list"
      >
        <svg
          className={styles.icon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <span className={styles.badge}>{allSaved ? 'SAVED FOR LATER' : 'CRITICAL'}</span>
        <span className={styles.summaryTitle}>{top.title}</span>
        {alerts.length > 1 && (
          <span
            className={styles.count}
            aria-label={
              alerts.length - active.length > 0
                ? `${active.length} unacknowledged, ${alerts.length - active.length} saved for later`
                : `${alerts.length} unacknowledged`
            }
            data-testid="critical-alert-count"
          >
            {active.length}
            {alerts.length - active.length > 0 && (
              <span className={styles.countSaved}> +{alerts.length - active.length} saved</span>
            )}
          </span>
        )}
        <span className={styles.spacer} />
        <span className={styles.toggle}>
          {expanded ? 'Hide' : more > 0 ? `+${more} more` : 'Review'}
          <svg
            className={expanded ? styles.chevUp : styles.chev}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {expanded && (
        <ul id="critical-alert-list" className={styles.list}>
          {alerts.map((alert) => {
            const isAcking = acking.has(alert.id);
            const state = stateOf(alert);
            const isSaved = state === 'saved_for_later';
            const isReturned = state === 'returned';
            return (
              <li
                key={alert.id}
                className={`${styles.row} ${isSaved ? styles.rowSaved : ''}`}
                data-testid={`critical-alert-row-${alert.id}`}
                data-state={state}
                data-target-notification-id={alert.id}
              >
                <div className={styles.rowText}>
                  <span className={styles.rowTitle}>{alert.title}</span>
                  {(() => {
                    const full = typeof alert.extra_data?.full_message === 'string' ? alert.extra_data.full_message : null;
                    const open = expandedMsg.has(alert.id);
                    return (
                      <>
                        <span className={open ? styles.rowMsgOpen : styles.rowMsg}>{open && full ? full : alert.message}</span>
                        {full && (
                          <button
                            type="button"
                            className={styles.moreBtn}
                            data-action="critical_alert_more"
                            data-testid={`alert-more-${alert.id}`}
                            onClick={() => setExpandedMsg((prev) => {
                              const next = new Set(prev);
                              if (next.has(alert.id)) next.delete(alert.id); else next.add(alert.id);
                              return next;
                            })}
                          >
                            {open ? 'Less' : 'More'}
                          </button>
                        )}
                      </>
                    );
                  })()}
                  {alert.recommended_action && (
                    <span className={styles.recommended}>
                      Suggested: {alert.recommended_action}
                    </span>
                  )}
                  {isSaved && (
                    <span className={styles.savedLine} data-testid={`saved-line-${alert.id}`}>
                      {savedForLaterLabel(alert)}
                    </span>
                  )}
                  {isReturned && (
                    <span className={styles.returnedLine} data-testid={`returned-line-${alert.id}`}>
                      {returnedNotes[alert.id] ?? 'You saved this for later; the date has passed and it is back.'}
                    </span>
                  )}
                  <span className={styles.rowTime}>
                    {formatRelativeTime(alert.created_at)}
                  </span>
                </div>
                <div className={styles.rowActions}>
                  {alert.link && (
                    <Link to={alert.link} className={styles.viewBtn} data-action="critical_alert_open">
                      View
                    </Link>
                  )}
                  {isSaved ? (
                    <button
                      type="button"
                      className={styles.viewBtn}
                      data-action="critical_alert_bring_back"
                      data-outcome="request"
                      data-testid={`bring-back-${alert.id}`}
                      onClick={() => handleBringBack(alert.id)}
                    >
                      Bring back
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={styles.viewBtn}
                      data-action="critical_alert_save_later_open"
                      data-testid={`save-later-${alert.id}`}
                      onClick={() => { setSaveError(null); setSaving(alert); }}
                      title="Keep it on the bar in a quieter colour, optionally until a date"
                    >
                      Save for later…
                    </button>
                  )}
                  {outcomeRequired.has(alert.id) ? (
                    <button
                      type="button"
                      className={styles.ackBtn}
                      data-action="critical_alert_resolve_open"
                      data-testid={`resolve-alert-${alert.id}`}
                      onClick={() => setResolving(alert)}
                    >
                      Resolve…
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={styles.ackBtn}
                      data-action="critical_alert_acknowledge"
                      data-outcome="request"
                      onClick={() => handleAcknowledge(alert.id)}
                      disabled={isAcking}
                    >
                      {isAcking ? 'Acknowledging…' : 'Acknowledge'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {resolving && (
        <AlertOutcomeDialog
          alert={resolving}
          onClose={() => setResolving(null)}
          onResolved={handleResolved}
        />
      )}

      {saving && (
        <SaveForLaterDialog
          alert={saving}
          submitting={saveSubmitting}
          error={saveError}
          onCancel={() => setSaving(null)}
          onSubmit={handleSaveForLater}
        />
      )}
    </div>
  );
}

export default CriticalAlertBar;
